// Placing pushed readings into cached pages, so a chart moves the moment a
// reading is stored instead of on its next refetch.
//
// A newest-first page is ordered by `telemetryId`, which increases with every
// stored measurement. A pushed reading newer than the page's head therefore
// belongs at its head; anything else (a reading committed out of order) is
// left to a refetch, so the cache never shows an order the server would not.

import type { ReadingCursor, ReadingWithCursor } from '@/api/endpoints';
import { compareIntegers } from '@/lib/json';

/** The readings screen's page size; its newest page takes pushed readings. */
export const READINGS_PAGE_SIZE = 100;

interface NewestPage<Cursor> {
  items: ReadingWithCursor[];
  nextBefore?: Cursor | null;
  hasMore: boolean;
}

/**
 * The page with `pushed` placed at its head and trimmed to `limit`, or null
 * when a pushed reading does not belong at the head and the page must be
 * refetched instead. Readings already on the page are ignored, so a repeated
 * event changes nothing.
 */
export function mergeNewest<Cursor, Page extends NewestPage<Cursor>>(
  page: Page,
  pushed: ReadingWithCursor[],
  limit: number,
  cursor: (telemetryId: ReadingCursor) => Cursor,
): Page | null {
  const known = new Set(page.items.map((item) => String(cursorKey(item.telemetryId))));
  const fresh = pushed
    .filter((item) => !known.has(String(cursorKey(item.telemetryId))))
    .sort((a, b) => compareIntegers(b.telemetryId, a.telemetryId));
  if (fresh.length === 0) return page;
  const head = page.items[0];
  const oldest = fresh[fresh.length - 1];
  if (head && oldest && compareIntegers(oldest.telemetryId, head.telemetryId) <= 0) return null;
  const merged = [...fresh, ...page.items];
  if (merged.length <= limit) return { ...page, items: merged };
  const kept = merged.slice(0, limit);
  const last = kept[kept.length - 1];
  return {
    ...page,
    items: kept,
    hasMore: true,
    nextBefore: last ? cursor(last.telemetryId) : (page.nextBefore ?? null),
  };
}

function cursorKey(value: ReadingCursor): string {
  return typeof value === 'number' ? String(value) : value.bigint.toString();
}
